/**
 * Formatting helpers for the board: clocks, zone and phase names, player labels and prompt sentences.
 * Sentence case everywhere (research 06 §2: uppercase only for eyebrows).
 */
import type { Phase, PlayerId, Prompt, ZoneRef } from '@play/protocol';

/** `m:ss` for a remaining time in milliseconds (never negative). */
export function formatClock(ms: number): string {
  const total = Math.max(0, Math.ceil(ms / 1000));
  const m = Math.floor(total / 60);
  const s = total % 60;
  return `${m}:${String(s).padStart(2, '0')}`;
}

/** Clock tone: amber under 30 s, red under 10 s (research 06 §8.4). */
export function clockTone(ms: number): 'normal' | 'low' | 'critical' {
  if (ms < 10_000) return 'critical';
  if (ms < 30_000) return 'low';
  return 'normal';
}

export const PHASE_LABELS: Readonly<Record<Phase, string>> = {
  awaken: 'Awaken',
  beginning: 'Beginning',
  channel: 'Channel',
  draw: 'Draw',
  main: 'Main', // the Main Phase (CR 316)
  ending: 'Ending',
};

const ZONE_LABELS: Readonly<Record<ZoneRef['kind'], string>> = {
  mainDeck: 'Main deck',
  runeDeck: 'Rune deck',
  hand: 'Hand',
  trash: 'Trash',
  banishment: 'Banishment',
  champion: 'Champion zone',
  legend: 'Legend',
  base: 'Base',
  runes: 'Runes',
  battlefield: 'Battlefield',
  facedown: 'Facedown',
  chain: 'Chain',
};

/** Short label of a zone kind ("Rune deck"). */
export function zoneKindLabel(kind: ZoneRef['kind']): string {
  return ZONE_LABELS[kind];
}

/** Battlefield labels by position: A, B, C… */
export function battlefieldLetter(index: number): string {
  return String.fromCharCode(65 + index);
}

/** A zone as a phrase relative to the viewer ("your hand", "opponent's trash", "Battlefield A"). */
export function zonePhrase(zone: ZoneRef, me: PlayerId | null, battlefieldIndex: (id: string) => number): string {
  if ('player' in zone) {
    const owner = zone.player === me ? 'your' : me === null ? `${zone.player.toUpperCase()}'s` : "opponent's";
    return `${owner} ${ZONE_LABELS[zone.kind].toLowerCase()}`;
  }
  if ('battlefield' in zone) {
    const letter = battlefieldLetter(Math.max(0, battlefieldIndex(zone.battlefield)));
    return zone.kind === 'facedown' ? `Battlefield ${letter} (facedown)` : `Battlefield ${letter}`;
  }
  return 'the chain';
}

/** Turns a structured prompt into a sentence (falls back to a readable form of its key). */
export function promptText(prompt: Prompt, names: PromptNames = PLAIN_NAMES): string {
  const template = PROMPTS[prompt.key];
  if (template) return template(prompt.params, names);
  // Card-authored labels (modes, "+2 Might") are already sentences: show them as written.
  if (!/^[A-Za-z][\w.-]*$/.test(prompt.key) || /^[A-Z+\-−\d]/.test(prompt.key)) return prompt.key;
  const words = prompt.key
    .replace(/^(rules|setup)\./, '')
    .replace(/[._-]+/g, ' ')
    .replace(/([a-z])([A-Z])/g, '$1 $2')
    .toLowerCase()
    .trim();
  const sentence = words.charAt(0).toUpperCase() + words.slice(1);
  return `${sentence}.`;
}

/** How prompt parameters that name things are shown (card ids → names, battlefield ids → "Battlefield A"). */
export interface PromptNames {
  card(cardId: string): string;
  battlefield(battlefieldId: string): string;
}

const PLAIN_NAMES: PromptNames = { card: (id) => id, battlefield: (id) => id };

type Params = Prompt['params'];
const num = (p: Params, k: string, d = 0): number => (typeof p[k] === 'number' ? p[k] : d);
const str = (p: Params, k: string): string => (typeof p[k] === 'string' ? p[k] : '');

const OPS: Readonly<Record<string, string>> = {
  draw: 'draw',
  deal: 'deal the damage',
  buff: 'buff',
  channel: 'channel',
  kill: 'kill',
  move: 'move',
  ready: 'ready',
  recall: 'recall',
  stun: 'stun',
};

/** What a target choice is for, by the instruction's op ("Choose what to deal the damage to."). */
const TARGET_VERBS: Readonly<Record<string, string>> = {
  deal: 'deal the damage to',
  modifyMight: 'change the Might of',
  kill: 'kill',
  stun: 'stun',
  returnToHand: 'return to its owner’s hand',
  move: 'move',
  recall: 'recall',
  buff: 'buff',
  grantKeyword: 'give it to',
  banish: 'banish',
  fight: 'fight with',
  counter: 'counter',
  ready: 'ready',
  exhaust: 'exhaust',
  heal: 'heal',
  recycle: 'recycle',
  choose: 'choose',
  attach: 'attach to',
  giveControl: 'take control of',
};

const COMPARISONS: Readonly<Record<string, string>> = { eq: '=', ne: '≠', lt: '<', lte: '≤', gt: '>', gte: '≥' };

/** A target group's constraints from the engine's `groupPromptParams` (" (same location, total Might ≤ 4)"). */
function groupRule(p: Params): string {
  const out: string[] = [];
  if (p['sameLocation'] === true) out.push('same location');
  if (p['sameController'] === true) out.push('same controller');
  if (p['onePerLocation'] === true) out.push('one per location');
  const might = str(p, 'totalMight');
  if (might)
    out.push(
      `total Might ${might
        .split(', ')
        .map((c) => c.replace(/^(\w+) /, (_, op: string) => `${COMPARISONS[op] ?? op} `))
        .join(', ')}`,
    );
  return out.length > 0 ? ` (${out.join(', ')})` : '';
}

/** What scoring a battlefield gives (CR 470.1, 473.1.b.1). */
function scoreGain(gains: string): string {
  if (gains === 'draw') return 'draw a card (final point)';
  if (gains === 'nothing') return 'no point (you can’t score points)';
  return '+1 point';
}

/** Tier-2 rules prompts (engine `rules/` decisions, reminders, recommendations and warnings). */
const RULES_PROMPTS: Readonly<Record<string, (p: Params, n: PromptNames) => string>> = {
  'rules.payCost': () => 'Pay the cost.',
  'rules.pay': () => 'Pay the cost.',
  'rules.payAdditional': (p) => `Pay the optional additional cost${str(p, 'label') ? ` (${str(p, 'label')})` : ''}?`,
  'rules.payAdditionalTimes': (p) =>
    `How many times do you pay the additional cost${str(p, 'label') ? ` (${str(p, 'label')})` : ''}?`,
  'rules.payAlternative': () => 'Play it for its alternative cost?',
  'rules.useDiscount': () => 'Use the once-a-turn discount for this card?',
  'rules.payAnyAmount': () => 'How much do you pay?',
  'rules.orderDeck': (p) =>
    str(p, 'position') === 'bottom'
      ? 'Order the cards to put on the bottom (the first goes lowest).'
      : 'Order the cards to put back on top (the first ends on top).',
  'rules.payToDo': (p) => `Pay to ${OPS[str(p, 'op')] ?? str(p, 'op')}?`,
  'rules.payOrCountered': (p) => `Pay ${num(p, 'energy')} or it is countered?`,
  'rules.playWhere': () => 'Where do you play it?',
  'rules.chooseDestination': () => 'Choose a destination.',
  'rules.chooseTargets': (p) => {
    const verb = TARGET_VERBS[str(p, 'op')];
    const base = verb ? `Choose what to ${verb}` : 'Choose targets';
    return `${base}${groupRule(p)}.`;
  },
  'rules.chooseObjects': () => 'Choose cards.',
  'rules.showOff': () => 'Show off? Reveal a card from your hand or pick a friendly one (optional).',
  'rules.chooseMode': () => 'Choose a mode.',
  'rules.choosePlayer': () => 'Choose a player.',
  'rules.player': (p) => str(p, 'player').toUpperCase(),
  'rules.chooseX': () => 'Choose X.',
  'rules.repeat': () => 'How many times do you repeat it?',
  'rules.youMay': (p) => `You may ${OPS[str(p, 'op')] ?? str(p, 'op')}. Do it?`,
  'rules.accelerate': () => 'Pay the Accelerate cost (it enters ready)?',
  'rules.splitDamage': (p) => `Split ${num(p, 'total')} damage among the targets.`,
  'rules.assignCombatDamage': () => 'Assign your combat damage.',
  'rules.discard': (p) => `Discard ${num(p, 'n', 1)}.`,
  'rules.topOrBottom': () => 'Top or bottom of the deck?',
  'rules.deckTop': () => 'Top',
  'rules.deckBottom': () => 'Bottom',
  'rules.spendBuffs': () => 'How many buffs do you spend?',
  'rules.orderReplacements': () => 'Order the replacement effects.',
  'rules.applyReplacement': () => 'Apply the replacement effect?',
  'rules.reminder.trigger': (p, n) => `${str(p, 'label') || 'Trigger'} of ${n.card(str(p, 'card'))}`,
  'rules.reminder.channel': () => 'Channel your runes',
  'rules.reminder.draw': () => 'Draw your card',
  'rules.recommend.trigger': (p, n) => `Resolve ${n.card(str(p, 'card'))}: ${str(p, 'label') || 'trigger'}`,
  'rules.recommend.channel': () => 'Channel',
  'rules.recommend.draw': () => 'Draw',
  'rules.recommend.resolve': (p, n) => `Resolve ${n.card(str(p, 'card'))}`,
  'rules.recommend.startCombat': (p, n) => `Start the combat at ${n.battlefield(str(p, 'battlefield'))}`,
  'rules.recommend.resolveCombat': (p, n) => `Resolve the combat at ${n.battlefield(str(p, 'battlefield'))}`,
  'rules.recommend.startShowdown': (p, n) => `Start the showdown at ${n.battlefield(str(p, 'battlefield'))}`,
  'rules.recommend.closeShowdown': (p, n) => `Close the showdown at ${n.battlefield(str(p, 'battlefield'))}`,
  'rules.recommend.conquer': (p, n) => `Conquer ${n.battlefield(str(p, 'battlefield'))}: ${scoreGain(str(p, 'gains'))}`,
  'rules.recommend.hold': (p, n) => `Hold ${n.battlefield(str(p, 'battlefield'))}: ${scoreGain(str(p, 'gains'))}`,
  'rules.recommend.cleanup': () => 'Clean up (lethal damage, empty battlefields)',
  'rules.warning': () => 'That looks illegal.',
  'rules.warning.abilityTiming': () => 'That ability can’t be used now.',
  'rules.warning.notAction': () => 'This is not an Action: it can’t be played now.',
  'rules.warning.notReaction': () => 'This is not a Reaction: it can’t be played now.',
  'rules.warning.exhaustedMove': () => 'Exhausted units can’t move.',
  'rules.warning.destination': () => 'That is not a legal destination.',
};

const PROMPTS: Readonly<Record<string, (p: Params, n: PromptNames) => string>> = {
  ...RULES_PROMPTS,
  mulligan: (p) => `Choose up to ${num(p, 'max', 2)} cards to recycle.`,
  'setup.mulligan': (p) => `Choose up to ${num(p, 'max', 2)} cards to recycle.`,
  chooseBattlefield: () => 'Choose your battlefield.',
  'setup.chooseBattlefield': () => 'Choose your battlefield.',
};
